import { useQuery } from "@tanstack/react-query";
import type {
	Barrel,
	BarrelLayout,
	PassId,
	RollMaterial,
	Stand,
} from "../models/Barrel";
import {
	type GetBarrelsParams,
	type GetPassIdsParams,
	getBarrels,
	getPassIds,
	getRollMaterials,
	getStands,
} from "../repositories/BarrelsRepository";

// ============================================
// Query Keys
// ============================================

export const BARRELS_QUERY_KEYS = {
	all: ["barrels"] as const,
	list: (params?: GetBarrelsParams) => ["barrels", "list", params] as const,
	detail: (id: number | null) => ["barrels", "detail", id] as const,
	stands: ["barrels", "stands"] as const,
	rollMaterials: ["barrels", "rollMaterials"] as const,
	passIds: (params?: GetPassIdsParams) =>
		["barrels", "passIds", params] as const,
};

// ============================================
// useBarrels Hook
// ============================================

interface UseBarrelsProps {
	search?: string | null;
	standId?: number | null;
	includeLayout?: boolean;
}

interface UseBarrelsResult {
	barrels: Barrel[];
	layouts: BarrelLayout[];
	isLoading: boolean;
	error: Error | null;
	refetch: () => void;
}

export const useBarrels = (props: UseBarrelsProps = {}): UseBarrelsResult => {
	const params: GetBarrelsParams = {
		search: props.search,
		standId: props.standId,
		onlyActives: false,
		includeLayout: props.includeLayout ?? false,
	};

	const query = useQuery({
		queryKey: BARRELS_QUERY_KEYS.list(params),
		queryFn: () => getBarrels(params),
	});

	return {
		barrels: query.data?.barrels ?? [],
		layouts: query.data?.layouts ?? [],
		isLoading: query.isLoading,
		error: query.error as Error | null,
		refetch: query.refetch,
	};
};

// ============================================
// useStands Hook (for dropdown)
// ============================================

export const useStands = () => {
	return useQuery<Stand[], Error>({
		queryKey: BARRELS_QUERY_KEYS.stands,
		queryFn: getStands,
		staleTime: 5 * 60 * 1000, // 5 minutes - stands don't change often
	});
};

// ============================================
// useRollMaterials Hook (for dropdown)
// ============================================

export const useRollMaterials = () => {
	return useQuery<RollMaterial[], Error>({
		queryKey: BARRELS_QUERY_KEYS.rollMaterials,
		queryFn: getRollMaterials,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};

// ============================================
// usePassIds Hook (for barrel layouts dropdown)
// ============================================

interface UsePassIdsProps {
	standId?: number | null;
	enabled?: boolean;
}

export const usePassIds = ({
	standId,
	enabled = true,
}: UsePassIdsProps = {}) => {
	const params: GetPassIdsParams = {
		standId,
		onlyActives: true,
	};

	return useQuery<PassId[], Error>({
		queryKey: BARRELS_QUERY_KEYS.passIds(params),
		queryFn: () => getPassIds(params),
		enabled,
		staleTime: 5 * 60 * 1000, // 5 minutes
	});
};
